import React, { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Chip,
  Divider,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { Add, Edit, Delete } from '@mui/icons-material';
import { teacherModuleAPI } from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';
import { useSupervisorTeacherContext } from '../../contexts/SupervisorTeacherContext';
import MakeupClassDialog from '../../components/Teacher/MakeupClassDialog';
import { buildBatchSessionPath } from '../../utils/teacherSessionNavigation';
import toast from 'react-hot-toast';
import { formatLocalYMD, shiftLocalYMD } from '../../utils/dateTimeUtils';
import { isCancelledSession, isHolidaySession } from '../../utils/sessionKindDisplay';

const makeupHasNoAttendance = (row) =>
  Number(row.attended_students || 0) === 0 && Number(row.absent_students || 0) === 0;

/** Missed regular classes can be made up. A makeup counts once the student is marked present. */
const makeupBalance = (row) => {
  const missed = Number(row.classesAbsent) || 0;
  const attended = Number(row.makeupsAttended) || 0;
  const left = row.makeupsLeft != null ? Number(row.makeupsLeft) || 0 : Math.max(0, missed - attended);
  return { missed, attended, left };
};

const TeacherMakeupClasses = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();
  const { isTeachingLead, isCountryManager } = useAuth();
  const picker = useSupervisorTeacherContext();
  const teacherIdParam = picker.isSupervisorPicker ? picker.selectedTeacherId : undefined;
  const showTeacherScopeAlert = (isTeachingLead() || isCountryManager()) && picker.isSupervisorPicker;

  const [fromDate, setFromDate] = useState(() => {
    const params = new URLSearchParams(typeof window !== 'undefined' ? window.location.search : '');
    return params.get('fromDate') || formatLocalYMD(new Date());
  });
  const [toDate, setToDate] = useState(() => {
    const params = new URLSearchParams(typeof window !== 'undefined' ? window.location.search : '');
    return params.get('toDate') || shiftLocalYMD(30);
  });

  useEffect(() => {
    const params = new URLSearchParams(location.search || '');
    const from = params.get('fromDate');
    const to = params.get('toDate');
    if (from) setFromDate(from);
    if (to) setToDate(to);
  }, [location.search]);
  const [historyMonths, setHistoryMonths] = useState(12);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editSession, setEditSession] = useState(null);
  const [prefill, setPrefill] = useState({ batchId: '', studentIds: [] });

  const listQuery = useQuery(
    ['teacherMakeupClasses', fromDate, toDate, historyMonths, teacherIdParam],
    async () => {
      const params = { fromDate, toDate, historyMonths };
      if (teacherIdParam) params.teacherId = teacherIdParam;
      const res = await teacherModuleAPI.getMakeupClasses(params);
      return res.data?.data || {};
    }
  );

  const batchesQuery = useQuery(
    ['teacherMyBatchesForMakeup', teacherIdParam],
    async () => {
      const params = teacherIdParam ? { teacherId: teacherIdParam } : undefined;
      const res = await teacherModuleAPI.getMyBatches(params);
      return res.data?.data?.batches || [];
    }
  );

  const cancelMutation = useMutation(
    (sessionId) => teacherModuleAPI.cancelMakeupClass(sessionId),
    {
      onSuccess: () => {
        toast.success('Makeup class cancelled');
        queryClient.invalidateQueries(['teacherMakeupClasses']);
      },
      onError: (err) => toast.error(err.response?.data?.message || 'Failed to cancel'),
    }
  );

  const d = listQuery.data || {};
  const statsWindow = d.statsWindow || {};
  const studentAttendance = useMemo(() => {
    const rows = d.studentAttendance || [];
    return [...rows].sort((a, b) => {
      const leftDiff = makeupBalance(b).left - makeupBalance(a).left;
      if (leftDiff) return leftDiff;
      return String(a.studentName || '').localeCompare(String(b.studentName || ''));
    });
  }, [d.studentAttendance]);
  const stillToSchedule = useMemo(
    () => studentAttendance.filter((r) => makeupBalance(r).left > 0),
    [studentAttendance]
  );
  const scheduledMakeups = d.scheduledMakeups || [];
  const makeupHistory = d.makeupHistory || [];
  const batchOptions = useMemo(() => {
    const batches = batchesQuery.data || [];
    const studentAttendance = listQuery.data?.studentAttendance || [];
    const map = new Map();
    batches.forEach((b) => map.set(b.id, { id: b.id, name: b.name }));
    studentAttendance.forEach((r) => {
      if (!map.has(r.batchId)) map.set(r.batchId, { id: r.batchId, name: r.batchName });
    });
    return [...map.values()].sort((a, b) => a.name.localeCompare(b.name));
  }, [batchesQuery.data, listQuery.data]);

  const openCreate = (batchId = '', studentIds = []) => {
    setEditSession(null);
    setPrefill({ batchId, studentIds });
    setDialogOpen(true);
  };

  const openEdit = (session) => {
    setEditSession(session);
    setPrefill({ batchId: '', studentIds: [] });
    setDialogOpen(true);
  };

  const openMakeupSession = (row) => {
    const path = buildBatchSessionPath(row.batch_id, row.id);
    if (path) navigate(path);
    else navigate(`/teacher/sessions/${row.id}`);
  };

  const formatStudents = (row) => {
    const list = row.scheduled_students || row.students || [];
    if (!Array.isArray(list) || list.length === 0) return '—';
    return list.map((s) => s.studentName || s.student_name).filter(Boolean).join(', ');
  };

  return (
    <Box>
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        justifyContent="space-between"
        alignItems={{ xs: 'flex-start', sm: 'center' }}
        spacing={1}
        sx={{ mb: 2 }}
      >
        <Box>
          <Typography variant="h4" fontWeight={700} gutterBottom>
            Makeup Classes
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Agree a time with the family, then schedule it here. The list shows who missed class in the
            last 3 months, how many makeup classes they already attended, and how many are still to schedule.
          </Typography>
        </Box>
        <Button variant="contained" startIcon={<Add />} onClick={() => openCreate()}>
          Schedule makeup class
        </Button>
      </Stack>

      {showTeacherScopeAlert ? (
        <Alert severity="info" sx={{ mb: 2 }}>
          Viewing makeup data for{' '}
          <strong>{d.viewingTeacherName || picker.selectedTeacherName || 'selected teacher'}</strong>.
          Use the teacher picker in the top bar to switch teachers.
        </Alert>
      ) : null}

      <Paper sx={{ p: 2, mb: 2 }}>
        <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 1 }}>
          Who still needs a makeup class
        </Typography>
        {statsWindow.start && statsWindow.end ? (
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
            {statsWindow.start} to {statsWindow.end}. Only students who have been in the batch for at least 3
            months. Missed classes can be made up. Still to schedule = missed classes minus makeup classes they
            already attended. A future booking counts after you mark the student present. Cancelled classes are
            listed separately and are not missed classes.
          </Typography>
        ) : null}
        {listQuery.isLoading ? (
          <Typography variant="body2" color="text.secondary">
            Loading attendance…
          </Typography>
        ) : (
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Student</TableCell>
                  <TableCell>Batch</TableCell>
                  <TableCell>With batch since</TableCell>
                  <TableCell align="right">Classes</TableCell>
                  <TableCell align="right">Attended</TableCell>
                  <TableCell align="right">Missed</TableCell>
                  <TableCell align="right">Not marked</TableCell>
                  <TableCell align="right">Cancelled</TableCell>
                  <TableCell>Makeup classes</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {studentAttendance.map((r) => {
                  const balance = makeupBalance(r);
                  return (
                  <TableRow key={`${r.studentId}-${r.batchId}`} hover selected={balance.left > 0}>
                    <TableCell>{r.studentName}</TableCell>
                    <TableCell>{r.batchName}</TableCell>
                    <TableCell>{r.assignedSince}</TableCell>
                    <TableCell align="right">{r.sessionsInWindow}</TableCell>
                    <TableCell align="right">{r.classesAttended}</TableCell>
                    <TableCell align="right">{balance.missed}</TableCell>
                    <TableCell align="right">{r.unmarkedPastSessions}</TableCell>
                    <TableCell align="right">{r.cancelledSessions ?? 0}</TableCell>
                    <TableCell>
                      <Stack spacing={0.25} alignItems="flex-start">
                        {balance.left > 0 ? (
                          <Chip
                            size="small"
                            color="warning"
                            label={`${balance.left} still to schedule`}
                            onClick={() => openCreate(r.batchId, [r.studentId])}
                          />
                        ) : balance.missed > 0 ? (
                          <Chip size="small" color="success" variant="outlined" label="All done" />
                        ) : (
                          <Chip size="small" variant="outlined" label="None missed" />
                        )}
                        <Typography variant="caption" color="text.secondary">
                          {balance.missed} missed · {balance.attended} attended
                        </Typography>
                      </Stack>
                    </TableCell>
                  </TableRow>
                  );
                })}
                {studentAttendance.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={9}>
                      <Typography variant="body2" color="text.secondary">
                        No students have been in your batches for at least 3 months.
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : null}
              </TableBody>
            </Table>
          </TableContainer>
        )}

        {stillToSchedule.length > 0 ? (
          <Box sx={{ mt: 2 }}>
            <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
              Still to schedule ({stillToSchedule.length})
            </Typography>
            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
              {stillToSchedule.map((r) => {
                const balance = makeupBalance(r);
                return (
                <Chip
                  key={`${r.studentId}-${r.batchId}`}
                  size="small"
                  color="warning"
                  variant="outlined"
                  label={`${r.studentName} · ${r.batchName} — ${balance.left} left (${balance.attended} attended)`}
                  onClick={() => openCreate(r.batchId, [r.studentId])}
                />
                );
              })}
            </Stack>
          </Box>
        ) : null}
      </Paper>

      <Paper sx={{ p: 2, mb: 2 }}>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems="center" sx={{ mb: 1 }}>
          <TextField
            label="From"
            type="date"
            value={fromDate}
            onChange={(e) => setFromDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
            size="small"
          />
          <TextField
            label="To"
            type="date"
            value={toDate}
            onChange={(e) => setToDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
            size="small"
          />
          <Button
            variant="outlined"
            onClick={() => {
              setFromDate(formatLocalYMD(new Date()));
              setToDate(shiftLocalYMD(30));
            }}
          >
            Reset range
          </Button>
        </Stack>
        <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 1 }}>
          Makeup sessions in date range
        </Typography>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Date</TableCell>
                <TableCell>Time</TableCell>
                <TableCell>Batch</TableCell>
                <TableCell>Students</TableCell>
                <TableCell align="right">Present / makeup</TableCell>
                <TableCell align="right">Absent</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {scheduledMakeups.map((r) => (
                <TableRow key={r.id} hover>
                  <TableCell>{r.session_date}</TableCell>
                  <TableCell>{`${String(r.start_time || '').slice(0, 5)} - ${String(r.end_time || '').slice(0, 5)}`}</TableCell>
                  <TableCell>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <span>{r.batch_name}</span>
                      <Chip size="small" label="Makeup" color="secondary" />
                      {isHolidaySession(r) ? (
                        <Chip size="small" label="Holiday" color="warning" />
                      ) : isCancelledSession(r) ? (
                        <Chip size="small" label="Cancelled" color="default" />
                      ) : r.is_upcoming ? (
                        <Chip size="small" label="Upcoming" variant="outlined" />
                      ) : null}
                    </Stack>
                  </TableCell>
                  <TableCell sx={{ maxWidth: 220 }}>{formatStudents(r)}</TableCell>
                  <TableCell align="right">{Number(r.attended_students || 0)}</TableCell>
                  <TableCell align="right">{Number(r.absent_students || 0)}</TableCell>
                  <TableCell align="right">
                    <Stack direction="row" spacing={0.5} justifyContent="flex-end">
                      <Button size="small" onClick={() => openMakeupSession(r)}>
                        Open
                      </Button>
                      {r.is_upcoming && makeupHasNoAttendance(r) && !isCancelledSession(r) && !isHolidaySession(r) ? (
                        <Button size="small" startIcon={<Edit />} onClick={() => openEdit(r)}>
                          Edit
                        </Button>
                      ) : null}
                      {makeupHasNoAttendance(r) && !isCancelledSession(r) && !isHolidaySession(r) ? (
                        <Button
                          size="small"
                          color="error"
                          startIcon={<Delete />}
                          onClick={() => {
                            if (window.confirm('Cancel this makeup class?')) {
                              cancelMutation.mutate(r.id);
                            }
                          }}
                        >
                          Cancel
                        </Button>
                      ) : null}
                    </Stack>
                  </TableCell>
                </TableRow>
              ))}
              {scheduledMakeups.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7}>
                    <Typography variant="body2" color="text.secondary">
                      No makeup sessions in this range. Use &quot;Schedule makeup class&quot; after agreeing a time offline.
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : null}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      <Paper sx={{ p: 2 }}>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems="center" sx={{ mb: 1 }}>
          <Typography variant="subtitle1" fontWeight={600}>
            Makeup history (completed sessions)
          </Typography>
          <TextField
            select
            label="Look back"
            size="small"
            value={String(historyMonths)}
            onChange={(e) => setHistoryMonths(Number(e.target.value))}
            sx={{ minWidth: 160 }}
            SelectProps={{ native: true }}
          >
            {[3, 6, 12, 24, 36].map((m) => (
              <option key={m} value={m}>
                {m} months
              </option>
            ))}
          </TextField>
        </Stack>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
          Past makeup classes only, including cancelled makeups, with how many students were marked present/makeup vs absent.
        </Typography>
        <Divider sx={{ mb: 1 }} />
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Date</TableCell>
                <TableCell>Time</TableCell>
                <TableCell>Batch</TableCell>
                <TableCell align="right">Present / makeup</TableCell>
                <TableCell align="right">Absent</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {makeupHistory.map((r) => (
                <TableRow key={r.id} hover>
                  <TableCell>{r.session_date}</TableCell>
                  <TableCell>{`${String(r.start_time || '').slice(0, 5)} - ${String(r.end_time || '').slice(0, 5)}`}</TableCell>
                  <TableCell>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <span>{r.batch_name}</span>
                      <Chip size="small" label="Makeup" color="secondary" />
                      {isHolidaySession(r) ? <Chip size="small" label="Holiday" color="warning" /> : null}
                      {isCancelledSession(r) ? <Chip size="small" label="Cancelled" /> : null}
                    </Stack>
                  </TableCell>
                  <TableCell align="right">{Number(r.attended_students || 0)}</TableCell>
                  <TableCell align="right">{Number(r.absent_students || 0)}</TableCell>
                  <TableCell align="right">
                    <Stack direction="row" spacing={0.5} justifyContent="flex-end">
                      <Button size="small" onClick={() => openMakeupSession(r)}>
                        Open
                      </Button>
                      {makeupHasNoAttendance(r) && !isCancelledSession(r) && !isHolidaySession(r) ? (
                        <Button
                          size="small"
                          color="error"
                          startIcon={<Delete />}
                          onClick={() => {
                            if (window.confirm('Cancel this makeup class?')) {
                              cancelMutation.mutate(r.id);
                            }
                          }}
                        >
                          Cancel
                        </Button>
                      ) : null}
                    </Stack>
                  </TableCell>
                </TableRow>
              ))}
              {makeupHistory.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6}>
                    <Typography variant="body2" color="text.secondary">
                      No completed makeup sessions in this period.
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : null}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      <MakeupClassDialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        onSaved={() => queryClient.invalidateQueries(['teacherMakeupClasses'])}
        batches={batchOptions}
        eligibleStudents={stillToSchedule}
        initialBatchId={prefill.batchId}
        initialStudentIds={prefill.studentIds}
        editSession={editSession}
      />
    </Box>
  );
};

export default TeacherMakeupClasses;
